import { type ChartConfiguration } from "@/components/ui/Chart/chart.types";
import { CHART_FONT } from "@/components/ui/Chart/chart.utils";

const { format: formatNumber } = new Intl.NumberFormat();

type Options = {
    getTotalValue?: (total: number) => string;
};

export type { Options as CreateDoughnutChartDisplayValuesPluginOptions };

export const createDoughnutChartDisplayValuesPlugin = (options?: Options) => {
    const getTotalValue = options?.getTotalValue ?? formatNumber;

    return {
        id: "doughnutChartDisplayValuesPlugin",
        afterDatasetsDraw(chart) {
            const {
                ctx,
                chartArea: { top, left, width, height },
            } = chart;

            const TEXT_HEIGHT = 12;
            const CENTER_TEXT_HEIGHT = 24;

            chart.data.datasets.forEach((_dataset, datasetIndex) => {
                const chartData = chart.data.datasets[datasetIndex].data as number[];

                const total = chartData.reduce((sum, next) => sum + next, 0);

                const centerX = width / 2 + left;
                const centerY = height / 2 + top;

                ctx.font = `${CENTER_TEXT_HEIGHT}px ${CHART_FONT}`;
                ctx.textAlign = "center";
                ctx.fillText(getTotalValue(total), centerX, centerY + CENTER_TEXT_HEIGHT * 0.3);
                ctx.font = `${TEXT_HEIGHT}px ${CHART_FONT}`;

                type Label = {
                    xStart: number;
                    yStart: number;
                    xEnd: number;
                    yEnd: number;
                    percentageText: string;
                    textWidth: number;
                    factorX: number;
                    factorY: number;
                };
                const labels: Label[] = [];

                chart.getDatasetMeta(datasetIndex).data.forEach((datapoint, datapointIndex) => {
                    const value = chartData[datapointIndex];

                    const percentage = (value / total) * 100;

                    const { startAngle, endAngle, outerRadius, innerRadius } = datapoint.getProps([
                        "startAngle",
                        "endAngle",
                        "outerRadius",
                        "innerRadius",
                    ]);
                    const { x, y } = datapoint.tooltipPosition(true);

                    const centerAngleRad = (startAngle + endAngle) / 2;
                    const lineLength = (outerRadius - innerRadius) * 0.6;

                    const factorX = Math.cos(centerAngleRad);
                    const factorY = Math.sin(centerAngleRad);

                    const xStart = x + factorX * lineLength * 0.5;
                    const yStart = y + factorY * lineLength * 0.5;

                    const xEnd = x + factorX * lineLength;
                    const yEnd = y + factorY * lineLength;

                    const percentageText = `${percentage.toFixed(2).replace(/\.0+$/, "")}%`;
                    const textWidth = ctx.measureText(percentageText).width;

                    // Check for overlap
                    const overlap = labels.some((label) => {
                        const dx = label.xStart - xEnd;
                        const dy = label.yStart - yEnd;
                        return Math.sqrt(dx * dx + dy * dy) < Math.max(textWidth, label.textWidth);
                    });

                    if (!overlap) {
                        labels.push({
                            xStart,
                            yStart,
                            xEnd,
                            yEnd,
                            percentageText,
                            textWidth,
                            factorX: Math.cos(centerAngleRad),
                            factorY: Math.sin(centerAngleRad),
                        });
                    }
                });

                labels.forEach((label) => {
                    const { xStart, yStart, xEnd, yEnd, percentageText, textWidth, factorX, factorY } = label;

                    // Draw path
                    ctx.beginPath();
                    ctx.moveTo(xStart, yStart);
                    ctx.lineTo(xEnd, yEnd);
                    ctx.stroke();

                    const textShiftX = (factorX * textWidth) / 2 + factorX * 4;
                    const textShiftY = factorY * TEXT_HEIGHT + factorY * 4;

                    ctx.font = `${TEXT_HEIGHT}px ${CHART_FONT}`;
                    ctx.textAlign = "center";
                    ctx.fillText(percentageText, xEnd + textShiftX, yEnd + textShiftY + TEXT_HEIGHT * 0.45);
                });
            });
        },
    } satisfies NonNullable<ChartConfiguration<"doughnut">["plugins"]>[number];
};
